import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {FormattedMessage, useIntl} from 'react-intl';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';

import {IStore} from 'redux/interface';
import {RoutesUrl} from 'utils/constants/routeConstants';
import {BannerStatus, STATUS_BANNER_CONFIG, STATUS_BANNER_DEFAULTS} from 'utils/constants/statusBanner';
import {
  getAssistantSession,
  getProgramAllowedManualEvents,
  getProgramCareTeam,
  getProgramDetail,
  getProgramLibrary,
  getProgramModules,
  getProgramNext,
  interventionAsyncActions
} from 'redux/pageAsyncActions';
import {fireManualEventAction} from 'redux/intervention/interventionAsyncActions';
import Loader from 'components/loader';
import {ITodayActivityItem, TodayActivity, isDueToday} from 'components/today-activity';
import {getCurrentModuleItem} from 'utils/helpers/getCurrentModuleItem';
import {useProgramDetailSse} from 'utils/hooks/useProgramDetailSse';
import {
  IAllowedManualEvent,
  IProgramCareTeamMember,
  IProgramDetail,
  IProgramLibraryItem,
  IProgramModule,
  IProgramModuleItem,
  IProgramNext,
  IProgramStatusChange,
  ModuleStatus,
  ProgramRunStatus,
  StepKind
} from '../../../redux/intervention/interfaces';

import './styles.scss';

const STATUS_BANNER_STATUSES = new Set<ProgramRunStatus>(['paused', 'withdrawn', 'completed', 'screen_failed']);

function formatDate(iso: string | null): string {
  if (!iso) {
    return '—';
  }

  return new Date(iso).toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: 'numeric'});
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

const MODULE_GLYPH: Record<ModuleStatus, string> = {
  completed: '✓',
  in_progress: '◐',
  available_not_started: '▸',
  locked: '◌',
  // UCSF-3571: reuses the same glyph as the top-level screen_failed banner (STATUS_BANNER_CONFIG
  // below) so the visual language stays consistent between the AP-level banner and this
  // module-level badge. Per-item status is never screen_failed — only a module's own rollup is.
  screen_failed: '–'
};

const EXPANDABLE_STATUSES = new Set<ModuleStatus>(['completed', 'in_progress', 'available_not_started', 'screen_failed']);

// ── Status Banner ─────────────────────────────────────────────────────────────
// STATUS_BANNER_CONFIG/BannerStatus/STATUS_BANNER_DEFAULTS live in
// utils/constants/statusBanner.ts, shared with the Home dashboard (UCSF-3536).

// ── Self-service lifecycle actions (UCSF-3546) ──────────────────────────────────
// Which manual event fires which affordance, and where. Gated at render time on
// whether that event code is present in the caller's own /allowed-manual-events set —
// the button never appears (or fires) unless the engine has actually allowed it.
const RESCREEN_EVENT_CODE = 'participant_rescreened';
const PAUSE_EVENT_CODE = 'participant_paused';
const RESUME_EVENT_CODE = 'participant_resumed';
const REACTIVATE_EVENT_CODE = 'participant_reactivated';

const BANNER_ACTION_EVENT_CODE: Partial<Record<BannerStatus, string>> = {
  screen_failed: RESCREEN_EVENT_CODE,
  paused: RESUME_EVENT_CODE,
  withdrawn: REACTIVATE_EVENT_CODE
};

const BANNER_ACTION_LABEL: Record<string, {label: string; defaultMessage: string}> = {
  [RESCREEN_EVENT_CODE]: {label: 'gritx.programDetail.action.rescreen', defaultMessage: 'Start screening again'},
  [RESUME_EVENT_CODE]: {label: 'gritx.programDetail.action.resume', defaultMessage: 'Resume'},
  [REACTIVATE_EVENT_CODE]: {label: 'gritx.programDetail.action.reactivate', defaultMessage: 'Reactivate'},
  [PAUSE_EVENT_CODE]: {label: 'gritx.programDetail.action.pause', defaultMessage: 'Pause'}
};

interface ConfirmActionButtonProps {
  eventCode: string;
  className: string;
  disabled: boolean;
  onConfirm: (eventCode: string) => void;
}

// A single lightweight "Are you sure?" step before firing — not the full reason-form flow, which
// none of these four events require (ManualEventPolicy.REQUIRES_REASON does not include them).
const ConfirmActionButton: React.FC<ConfirmActionButtonProps> = ({eventCode, className, disabled, onConfirm}) => {
  const [pendingConfirm, setPendingConfirm] = useState(false);
  const {label, defaultMessage} = BANNER_ACTION_LABEL[eventCode];

  if (pendingConfirm) {
    return (
      <span className="manual-event-action manual-event-action--confirming">
        <span className="manual-event-action__prompt">
          <FormattedMessage id="gritx.programDetail.action.areYouSure" defaultMessage="Are you sure?"/>
        </span>
        <button
          type="button"
          className="manual-event-action__confirm-btn"
          disabled={disabled}
          onClick={() => {
            setPendingConfirm(false);
            onConfirm(eventCode);
          }}
        >
          <FormattedMessage id="gritx.programDetail.action.yes" defaultMessage="Yes"/>
        </button>
        <button
          type="button"
          className="manual-event-action__cancel-btn"
          onClick={() => setPendingConfirm(false)}
        >
          <FormattedMessage id="gritx.programDetail.action.cancel" defaultMessage="Cancel"/>
        </button>
      </span>
    );
  }

  return (
    <button type="button" className={className} disabled={disabled} onClick={() => setPendingConfirm(true)}>
      <FormattedMessage id={label} defaultMessage={defaultMessage}/>
    </button>
  );
};

interface StatusBannerProps {
  status: BannerStatus;
  statusChange: IProgramStatusChange | null;
  allowedEventCodes: Set<string>;
  actionPending: boolean;
  onFireEvent: (eventCode: string) => void;
}

const StatusBanner: React.FC<StatusBannerProps> = ({status, statusChange, allowedEventCodes, actionPending, onFireEvent}) => {
  const config = STATUS_BANNER_CONFIG[status];
  const actorName = statusChange?.actorName;
  const changedAt = statusChange?.changedAt;
  const reason = statusChange?.reason ?? undefined;
  const actionEventCode = BANNER_ACTION_EVENT_CODE[status];
  const showAction = actionEventCode != null && allowedEventCodes.has(actionEventCode);

  return (
    <div className={`status-banner status-banner--${status}`} role="status">
      <span className="status-banner__icon">{config.icon}</span>
      <div className="status-banner__body">
        <div className="status-banner__title">
          <FormattedMessage id={config.title} defaultMessage={STATUS_BANNER_DEFAULTS[status]}/>
        </div>
        {(actorName || changedAt) && (
          <div className="status-banner__meta">
            {actorName && <span>{actorName}</span>}
            {actorName && changedAt && ' · '}
            {changedAt && <span>{formatDateTime(changedAt)}</span>}
          </div>
        )}
        {reason && <div className="status-banner__reason">{reason}</div>}
      </div>
      {showAction && (
        <ConfirmActionButton
          eventCode={actionEventCode as string}
          className="status-banner__action"
          disabled={actionPending}
          onConfirm={onFireEvent}
        />
      )}
    </div>
  );
};

// ── Next-up Card ──────────────────────────────────────────────────────────────

interface NextUpCardProps {
  next: IProgramNext;
  onStart: (stepId: number) => void;
}

const NextUpCard: React.FC<NextUpCardProps> = ({next, onStart}) => {
  if (next.locked) {
    const lockedLabel = next.availableAt
      ? <FormattedMessage id="gritx.programDetail.availableAt" defaultMessage="Available {date}" values={{date: formatDateTime(next.availableAt)}}/>
      : <FormattedMessage id="gritx.programDetail.scheduleLocked" defaultMessage="Not yet available"/>;

    return (
      <div className="next-up-card next-up-card--locked">
        <div className="next-up-card__label">
          <FormattedMessage id="gritx.programDetail.nextUp" defaultMessage="Up next"/>
        </div>
        <div className="next-up-card__available-at">{lockedLabel}</div>
      </div>
    );
  }

  return (
    <div className="next-up-card">
      <div className="next-up-card__label">
        <FormattedMessage id="gritx.programDetail.nextUp" defaultMessage="Up next"/>
      </div>
      <div className="next-up-card__title">{next.title}</div>
      {next.estimatedMinutes != null && (
        <div className="next-up-card__meta">
          <FormattedMessage
            id="gritx.programDetail.estimatedMinutes"
            defaultMessage="{n} min"
            values={{n: next.estimatedMinutes}}
          />
        </div>
      )}
      <button
        className="next-up-card__action"
        onClick={() => onStart(next.stepId)}
        aria-label="Start step"
      >
        <FormattedMessage id="gritx.programDetail.start" defaultMessage="Start ▸"/>
      </button>
    </div>
  );
};

// ── Module item ───────────────────────────────────────────────────────────────

const ITEM_CLICKABLE = new Set<ModuleStatus>(['completed', 'in_progress', 'available_not_started']);

interface ModuleItemRowProps {
  item: IProgramModuleItem;
  onItemClick: (itemId: number, status: ModuleStatus, stepKind: StepKind) => void;
}

const ModuleItemRow: React.FC<ModuleItemRowProps> = ({item, onItemClick}) => {
  const clickable = ITEM_CLICKABLE.has(item.status);

  const itemContent = (
    <>
      <span className="module-item__glyph">{MODULE_GLYPH[item.status]}</span>
      <span className="module-item__title">{item.title}</span>
    </>
  );

  return (
    <>
      <li className={`module-item${clickable ? ' module-item--clickable' : ''}`}>
        {clickable
          ? (
            <button
              className="module-item__button"
              onClick={() => onItemClick(item.itemId, item.status, item.stepKind)}
            >
              {itemContent}
            </button>
          )
          : itemContent
        }
      </li>
      {item.status === 'locked' && item.lockedReason && (
        <span className="module-item__locked-reason">{item.lockedReason}</span>
      )}
    </>
  );
};

// ── Module row ────────────────────────────────────────────────────────────────

interface ModuleRowProps {
  module: IProgramModule;
  expanded: boolean;
  onToggle: (moduleId: number) => void;
  onItemClick: (itemId: number, status: ModuleStatus, stepKind: StepKind) => void;
}

const ModuleRow: React.FC<ModuleRowProps> = ({module, expanded, onToggle, onItemClick}) => {
  const expandable = EXPANDABLE_STATUSES.has(module.status);

  const handleHeaderKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      onToggle(module.moduleId);
    }
  };

  const interactiveHeaderProps = expandable
    ? {
      onClick: () => onToggle(module.moduleId),
      role: 'button' as const,
      tabIndex: 0,
      'aria-expanded': expanded,
      onKeyDown: handleHeaderKeyDown
    }
    : {};

  return (
    <div className="intervention-card module-row">
      <div
        className={`intervention-card__header${expandable ? ' intervention-card__header--clickable' : ''}`}
        {...interactiveHeaderProps}
      >
        <span className="module-row__glyph">{MODULE_GLYPH[module.status]}</span>
        <span className="intervention-card__name">{module.title}</span>
        <span className={`module-row__status-pill module-row__status-pill--${module.status}`}>
          {module.status.replace(/_/g, ' ')}
        </span>
        {expandable && (
          <span className={`module-row__chevron${expanded ? ' module-row__chevron--open' : ''}`}>▼</span>
        )}
      </div>

      <div className="module-row__meta">
        {module.completedCount}/{module.itemCount}
        {module.lastActivityAt && ` · ${formatDate(module.lastActivityAt)}`}
        {module.unlockHint && ` · ${module.unlockHint}`}
      </div>

      {expandable && expanded && module.items.length > 0 && (
        <ul className="module-row__items">
          {module.items.map((item) => (
            <ModuleItemRow key={item.itemId} item={item} onItemClick={onItemClick}/>
          ))}
        </ul>
      )}
    </div>
  );
};

// ── Modules section ───────────────────────────────────────────────────────────

interface ModulesSectionProps {
  modules: IProgramModule[];
  isLoading: boolean;
  expandedModules: Set<number>;
  onToggle: (moduleId: number) => void;
  onItemClick: (itemId: number, status: ModuleStatus, stepKind: StepKind) => void;
}

const ModulesSection: React.FC<ModulesSectionProps> = ({modules, isLoading, expandedModules, onToggle, onItemClick}) => {
  if (isLoading && modules.length === 0) {
    return <Loader nested/>;
  }

  return (
    <section className="interventions__section">
      <h2 className="interventions__section-title">
        <FormattedMessage id="gritx.programDetail.modules" defaultMessage="Modules"/>
      </h2>
      <div className="interventions__cards">
        {modules.map((mod) => (
          <ModuleRow
            key={mod.moduleId}
            module={mod}
            expanded={expandedModules.has(mod.moduleId)}
            onToggle={onToggle}
            onItemClick={onItemClick}
          />
        ))}
      </div>
    </section>
  );
};

// ── Resources section ─────────────────────────────────────────────────────────

const ResourcesSection: React.FC<{items: IProgramLibraryItem[]}> = ({items}) => {
  if (items.length === 0) {
    return null;
  }

  return (
    <section className="interventions__section">
      <h2 className="interventions__section-title">
        <FormattedMessage id="gritx.programDetail.resources" defaultMessage="Resources"/>
      </h2>
      <ul className="interventions__medications">
        {items.map((item) => (
          <li key={item.contentId} className="resource-item">
            <span className="resource-item__type">{item.contentType}</span>
            <span className="resource-item__title">{item.title}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};

// ── Today's activity ─────────────────────────────────────────────────────────

interface ITodaysActivitySectionProps {
  modules: IProgramModule[];
  onItemClick: (itemId: number, status: ModuleStatus, stepKind: StepKind) => void;
}

const TodaysActivitySection: React.FC<ITodaysActivitySectionProps> = ({modules, onItemClick}) => {
  const items = useMemo(() => {
    const collected: ITodayActivityItem[] = [];

    modules.forEach((module) => {
      const item = getCurrentModuleItem(module);

      if (item && isDueToday(item.availableAt)) {
        collected.push({
          key: `${module.moduleId}-${item.itemId}-${item.stepKind}`,
          title: item.title,
          context: module.title,
          time: item.availableAt,
          onClick: () => onItemClick(item.itemId, item.status, item.stepKind)
        });
      }
    });

    return collected;
  }, [modules, onItemClick]);

  return (
    <section className="interventions__section">
      <h2 className="interventions__section-title">
        <FormattedMessage id="gritx.programDetail.todaysActivity" defaultMessage="Today's activity"/>
      </h2>
      <div className="program-detail__activity-list">
        <TodayActivity items={items}/>
      </div>
    </section>
  );
};

// ── Care team section ─────────────────────────────────────────────────────────

const CareTeamSection: React.FC<{members: IProgramCareTeamMember[]}> = ({members}) => {
  const intl = useIntl();

  if (members.length === 0) {
    return null;
  }

  return (
    <section className="interventions__section">
      <h2 className="interventions__section-title">
        <FormattedMessage id="gritx.programDetail.careTeam" defaultMessage="Care Team"/>
      </h2>
      <ul className="interventions__care-team">
        {members.map((m) => (
          <li key={m.userId} className="care-team-member">
            <span className="care-team-member__name">{m.name}</span>
            <span className="care-team-member__role">{m.roleLabel}</span>
            {m.canMessage && (
              <button
                className="care-team-member__message-btn"
                aria-label={intl.formatMessage(
                  {id: 'gritx.programDetail.messageProvider', defaultMessage: 'Message {name}'},
                  {name: m.name}
                )}
                onClick={() => undefined}
              >
                <FormattedMessage id="gritx.programDetail.message" defaultMessage="Message"/>
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

// ── Display-flag helpers ──────────────────────────────────────────────────────

function getDayLabel(
  daysElapsed: number,
  estimatedDurationDays: number | null,
  intl: ReturnType<typeof useIntl>
): string {
  if (estimatedDurationDays != null) {
    return intl.formatMessage(
      {id: 'gritx.programDetail.dayOf', defaultMessage: 'Day {n} of ~{total}'},
      {n: daysElapsed, total: estimatedDurationDays}
    );
  }

  return intl.formatMessage(
    {id: 'gritx.programDetail.day', defaultMessage: 'Day {n}'},
    {n: daysElapsed}
  );
}

export function getDisplayFlags(detail: IProgramDetail, nextLoaded: boolean) {
  const showStatusBanner = STATUS_BANNER_STATUSES.has(detail.status);
  const isStarted = detail.startedAt !== null;

  return {
    showStatusBanner,
    showNextUp: !showStatusBanner && isStarted && detail.status === 'in_progress' && nextLoaded,
    showStartingSoon: !showStatusBanner && !isStarted,
    // UCSF-3546: screen_failed used to hide modules alone, out of step with paused/withdrawn/
    // completed (which all show them). A screen_failed AP can still have completed module history
    // worth showing, and the rescreen action lives in the banner above — modules are always shown.
    showModules: true
  };
}

// ── Header ────────────────────────────────────────────────────────────────────

interface IProgramHeaderProps {
  detail: IProgramDetail;
  dayLabel: string;
  showPause: boolean;
  actionPending: boolean;
  onFireEvent: (eventCode: string) => void;
}

const ProgramHeader: React.FC<IProgramHeaderProps> = ({detail, dayLabel, showPause, actionPending, onFireEvent}) => (
  <header className="program-header">
    <h1 className="interventions__title">
      <Link
        to={RoutesUrl.Interventions}
        className="program-detail__back"
        aria-label="Back to interventions"
      >‹</Link>
      {detail.intervention.name}
    </h1>
    <div className="program-header__meta">
      {detail.intervention.versionLabel && (
        <span className="program-header__version">{detail.intervention.versionLabel}</span>
      )}
      {detail.startedAt && (
        <>
          <span className="program-header__meta-sep" aria-hidden="true">·</span>
          <span>
            <FormattedMessage id="gritx.programDetail.started" defaultMessage="Started"/>
            {' '}{formatDate(detail.startedAt)}
          </span>
        </>
      )}
      {detail.progress.daysElapsed != null && (
        <>
          <span className="program-header__meta-sep" aria-hidden="true">·</span>
          <span className="program-header__day">{dayLabel}</span>
        </>
      )}
    </div>
    {detail.progress.percentComplete != null && (
      <div
        className="intervention-progress-bar"
        role="progressbar"
        aria-valuenow={detail.progress.percentComplete}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="intervention-progress-fill" style={{width: `${detail.progress.percentComplete}%`}}/>
      </div>
    )}
    {showPause && (
      <div className="program-header__actions">
        <ConfirmActionButton
          eventCode={PAUSE_EVENT_CODE}
          className="program-header__action"
          disabled={actionPending}
          onConfirm={onFireEvent}
        />
      </div>
    )}
  </header>
);

// ── Main page ─────────────────────────────────────────────────────────────────

export const ProgramDetail: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const {apId: apIdParam} = useParams<{apId: string}>();
  const apId = Number(apIdParam);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const intl = useIntl();

  const [expandedModules, setExpandedModules] = useState<Set<number>>(new Set());
  const [actionPending, setActionPending] = useState(false);

  const {loading} = useSelector((state: IStore) => state.status);
  const {detail, nextStep, modules, libraryItems, careTeam, allowedEvents} = useSelector((state: IStore) => {
    const s = state.intervention;
    const rawModules = s.programModules[apId];
    const rawLibrary = s.programLibrary[apId];
    const rawCareTeam = s.programCareTeam[apId];
    const rawAllowedEvents = s.programAllowedManualEvents[apId];

    return {
      detail: s.programDetail[apId],
      nextStep: s.programNext[apId],
      modules: Array.isArray(rawModules) ? rawModules : [],
      libraryItems: Array.isArray(rawLibrary) ? rawLibrary : [],
      careTeam: Array.isArray(rawCareTeam) ? rawCareTeam : [],
      allowedEvents: Array.isArray(rawAllowedEvents) ? rawAllowedEvents : []
    };
  }, shallowEqual);

  const allowedEventCodes = useMemo(
    () => new Set(allowedEvents.map((e: IAllowedManualEvent) => e.eventCode)),
    [allowedEvents]
  );

  useEffect(() => {
    if (!apId) {
      return;
    }
    dispatch(interventionAsyncActions[getProgramDetail](apId));
    dispatch(interventionAsyncActions[getProgramNext](apId));
    dispatch(interventionAsyncActions[getProgramModules](apId));
    dispatch(interventionAsyncActions[getProgramLibrary](apId));
    dispatch(interventionAsyncActions[getProgramCareTeam](apId));
    // This page no longer renders its own Assistant FAB, but StepContent.tsx (reached only by
    // navigating from here) reads assistantActive without ever fetching it itself -- keep this
    // dispatch so that page still sees it populated (UCSF-3538).
    dispatch(interventionAsyncActions[getAssistantSession]());
    dispatch(interventionAsyncActions[getProgramAllowedManualEvents](apId));
  }, [apId, dispatch]);

  const handleFireManualEvent = useCallback((eventCode: string) => {
    setActionPending(true);
    dispatch(fireManualEventAction(apId, eventCode, undefined, () => {
      setActionPending(false);
      dispatch(interventionAsyncActions[getProgramDetail](apId));
      dispatch(interventionAsyncActions[getProgramAllowedManualEvents](apId));
    }, () => setActionPending(false)));
  }, [apId, dispatch]);

  useProgramDetailSse(apId);

  const toggleModule = useCallback((moduleId: number) => {
    setExpandedModules((prev) => {
      const updated = new Set(prev);

      if (updated.has(moduleId)) {
        updated.delete(moduleId);
      } else {
        updated.add(moduleId);
      }

      return updated;
    });
  }, []);

  const handleItemClick = useCallback((itemId: number, _status: ModuleStatus, stepKind: StepKind) => {
    // UCSF-3496: a Recurring/Multi-instance module's item is addressed by a step_item_progress_id,
    // not a PAS id — patient-step-session needs stepKind to tell the two id-spaces apart.
    const query = stepKind === 'sip' ? '?stepKind=sip' : '';

    navigate(`/interventions/${apId}/steps/${itemId}${query}`);
  }, [apId, navigate]);

  const handleStartStep = useCallback((stepId: number) => {
    navigate(`/interventions/${apId}/steps/${stepId}`);
  }, [apId, navigate]);

  if (!detail) {
    if (loading.includes(getProgramDetail)) {
      return <div className="program-detail"><Loader nested/></div>;
    }

    return (
      <div className="program-detail">
        <nav className="program-detail__breadcrumb">
          <Link to={RoutesUrl.Interventions}>
            <FormattedMessage id="gritx.programDetail.backHome" defaultMessage="‹ Home"/>
          </Link>
        </nav>
        <p>
          <FormattedMessage id="gritx.programDetail.notFound" defaultMessage="Program not found."/>
        </p>
      </div>
    );
  }

  const {showStatusBanner, showNextUp, showStartingSoon, showModules} = getDisplayFlags(
    detail, nextStep !== undefined
  );
  const dayLabel = detail.progress.daysElapsed != null
    ? getDayLabel(detail.progress.daysElapsed, detail.estimatedDurationDays, intl)
    : '';

  return (
    <div className="program-detail">
      <ProgramHeader
        detail={detail}
        dayLabel={dayLabel}
        showPause={!showStatusBanner && allowedEventCodes.has(PAUSE_EVENT_CODE)}
        actionPending={actionPending}
        onFireEvent={handleFireManualEvent}
      />

      {showStatusBanner && (
        <StatusBanner
          status={detail.status as BannerStatus}
          statusChange={detail.statusChange}
          allowedEventCodes={allowedEventCodes}
          actionPending={actionPending}
          onFireEvent={handleFireManualEvent}
        />
      )}

      {showNextUp && (
        <NextUpCard next={nextStep as IProgramNext} onStart={handleStartStep}/>
      )}

      {showStartingSoon && (
        <div className="next-up-card next-up-card--locked">
          <div className="next-up-card__label">
            <FormattedMessage id="gritx.programDetail.nextUp" defaultMessage="Up next"/>
          </div>
          <div className="next-up-card__available-at">
            <FormattedMessage id="gritx.programDetail.startingSoon" defaultMessage="Starting soon"/>
          </div>
        </div>
      )}

      {!showStatusBanner && modules.length > 0 && (
        <TodaysActivitySection modules={modules} onItemClick={handleItemClick}/>
      )}

      {showModules && (
        <ModulesSection
          modules={modules}
          isLoading={loading.includes(getProgramModules)}
          expandedModules={expandedModules}
          onToggle={toggleModule}
          onItemClick={handleItemClick}
        />
      )}

      <ResourcesSection items={libraryItems}/>

      <CareTeamSection members={careTeam}/>
    </div>
  );
};
